<template>
  <div class="s-credit-device">
    <SCombobox
      v-if="supportsSearch"
      v-model="companyName"
      blur-on-select
      disable-filter
      :error-label="tr('common_fe.fields.company_name.error_label')"
      :items="optionItems"
      :label="tr('common_fe.fields.company_name.label')"
      :loading="loading"
      name="companyName"
      :placeholder="tr('common_fe.fields.company_name.placeholder')"
      prefix-icon="fa-regular fa-search"
      required
      :rules="[companyNameFoundRule()]"
      suffix-icon=""
      :tooltip="tr('common_fe.fields.company_name.tooltip')"
      :validation-events="eager"
      v-on="gtmListeners"
    />

    <STextInput
      v-else
      v-model="companyName"
      :label="tr('common_fe.fields.company_name.label')"
      name="companyName"
      :placeholder="tr('common_fe.fields.company_name.placeholder')"
      required
      v-on="gtmListeners"
    />

    <STextInput
      v-if="showManual || !supportsSearch"
      v-model="registrationNumber"
      class="s-credit-device__registration-number"
      :error-label="tr('common_fe.fields.registration_number.error_label')"
      :label="tr('common_fe.fields.registration_number.label')"
      :minlength="8"
      name="registrationNumber"
      show-required-type="optional"
      :validation-events="eager"
      v-on="gtmListeners"
    />
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue";

import { computedAsync, until } from "@vueuse/core";
import { computed, ref, watch } from "vue";

import type { LocaleMorpheus } from "@solvari/translations";

import { SCombobox } from "@solvari/common-fe";
import { reEmit, useModel } from "@solvari/common-fe/composables";
import {
  defineRule,
  eager,
  onCreatedIfValue,
} from "@solvari/common-fe/validation";
import { useI18nCommonFe } from "@solvari/translations";
import { asyncThrottle, debounce, isError } from "@solvari/utils";

import type { CompanyDetail } from "@/organisms/credit-device/creditDevice.api.ts";

import STextInput from "@/molecules/STextInput.vue";
import { searchCompanies } from "@/organisms/credit-device/creditDevice.api.ts";

const { tr } = useI18nCommonFe();

const props = defineProps({
  companyName: { type: String as PropType<string | null>, default: null },
  registrationNumber: {
    type: String as PropType<string | null>,
    default: null,
  },
  locale: {
    type: String as PropType<LocaleMorpheus>,
    default: "nl-NL",
  },
});

const emit = defineEmits<{
  (event: "blur", name: string): void;
  (event: "focus", name: string): void;

  (event: "update:city", city: string | null): void;
  (event: "update:companyName", companyName: string | null): void;
  (event: "update:creditDeviceId", creditDeviceId: number | null): void;
  (event: "update:houseNumber", houseNumber: string | null): void;
  (event: "update:registrationNumber", registrationNumber: string | null): void;

  (event: "update:street", street: string | null): void;
  (event: "update:zipcode", zipcode: string | null): void;
  (event: "validationError", params: { error: string; name: string }): void;
}>();

const gtmListeners = reEmit(emit, ["focus", "blur", "validationError"]);

const companyName = useModel("companyName", props, emit);
const registrationNumber = useModel("registrationNumber", props, emit);

const showManual = ref(false);
const loading = ref(false);
function shouldSearch(companyName: string | null): companyName is string {
  return !!companyName && companyName.length > 1;
}

const autocomplete = debounce(asyncThrottle(searchCompanies), 500);
const foundCompanies = computedAsync<CompanyDetail[]>(
  async () => {
    if (!shouldSearch(companyName.value)) {
      return [];
    }

    const noSpecialCharactersRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/g;
    const result = await autocomplete({
      companyName: companyName.value.replaceAll(noSpecialCharactersRegex, ""),
      locale: props.locale,
    });

    if (isError(result)) {
      return [];
    }

    return result;
  },
  [],
  { lazy: true, evaluating: loading },
);

const supportsSearch = computed(() =>
  ["be-NL", "be-FR", "nl-NL"].includes(props.locale),
);

const selected = ref<CompanyDetail | null>(null);
const match = computed(() => {
  return (
    foundCompanies.value.find((item) => {
      return item.companyName === companyName.value;
    }) ?? null
  );
});

const optionItems = computed(() => {
  const foundOptions = foundCompanies.value.map((item) => ({
    label: `${item.companyName} (${item.companyId}) - ${item.city}`,
    value: item.companyName,
  }));

  if (!shouldSearch(companyName.value) || !!match.value) {
    return foundOptions;
  }

  // TODO: Add slots to SListbox to prevent this hack
  if (loading.value) {
    return [
      {
        label: tr("common_fe.fields.company_name.loading"),
        value: "",
        disabled: true,
      },
      ...foundOptions,
    ];
  }

  return [
    ...foundOptions,
    {
      label: tr("common_fe.fields.company_name.not_found"),
      value: companyName.value,
    },
  ];
});

const companyNameFoundRule = defineRule(attemptApplyCompany, {
  name: "exists",
  events: onCreatedIfValue(["blur"]),
  color: "warning",
  blocking: false,
  message: () => tr("common_fe.fields.company_name.validation.exists"),
});

async function attemptApplyCompany() {
  if (!props.companyName) {
    selected.value = null;
    showManual.value = false;
    return false;
  }
  await until(loading).toBe(false);
  if (match.value) {
    selected.value = match.value;
    showManual.value = false;
    return true;
  }

  selected.value = null;
  showManual.value = true;
  return false;
}

watch(selected, () => {
  companyName.value = selected.value?.companyName ?? companyName.value;
  registrationNumber.value = selected.value?.companyId.toString() ?? "";

  emit("update:creditDeviceId", selected.value?.id ?? null);
  emit("update:zipcode", selected.value?.zipCode ?? null);
  emit("update:houseNumber", selected.value?.houseNr ?? null);
  emit("update:city", selected.value?.city ?? null);
  emit("update:street", selected.value?.street ?? null);
});
</script>

<style lang="postcss">
.s-credit-device__registration-number {
  @apply mt-4;
}
</style>
